// A world atlas (issue 1173): the DM's trail through linked scenes — world › region › town — for the "Back" breadcrumb.
// CLIENT STATE, per DM window, never stored: it is how this DM came here in this sitting, and a reload or a second
// window starts with none (the Scenes panel reaches every scene anyway). Each step remembers whether the DM moved the
// table along it ("Go there") or only opened the next scene for themselves ("Open"), so Back retraces it the same way.
import { useSyncExternalStore } from 'react';

export interface AtlasStep { sceneId: string; name: string; moved: boolean }

let trail: AtlasStep[] = [];
const subs = new Set<() => void>();
const tell = () => { for (const s of subs) s(); };

export const atlasTrail = {
  get: (): AtlasStep[] => trail,
  subscribe(fn: () => void): () => void { subs.add(fn); return () => { subs.delete(fn); }; },
  /** Followed a link away from `from`: it joins the trail (a scene already on it cuts the trail back to there first). */
  follow(from: { sceneId: string; name: string }, moved: boolean): void {
    const at = trail.findIndex((s) => s.sceneId === from.sceneId);
    trail = [...(at >= 0 ? trail.slice(0, at) : trail), { sceneId: from.sceneId, name: from.name, moved }];
    tell();
  },
  /** Back to step `i`: it and everything after it leave the trail; returns whether any step after it moved the table. */
  backTo(i: number): boolean {
    const moved = trail.slice(i).some((s) => s.moved);
    trail = trail.slice(0, i);
    tell();
    return moved;
  },
  /** The trail drops scenes the table no longer has. */
  prune(existing: ReadonlySet<string>): void {
    if (trail.every((s) => existing.has(s.sceneId))) return;
    trail = trail.filter((s) => existing.has(s.sceneId));
    tell();
  },
  clear(): void { trail = []; tell(); },
};

export const useAtlasTrail = (): AtlasStep[] => useSyncExternalStore(atlasTrail.subscribe, atlasTrail.get, atlasTrail.get);
